// src/components/EngineerDetailPage.js
import React, { useState, useEffect, useMemo } from "react";
import { db } from "../firebase";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  Grid,
  Avatar,
  Card,
  CardContent,
  Stack,
  Divider,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import EmailIcon from "@mui/icons-material/Email";
import EngineeringIcon from "@mui/icons-material/Engineering";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonIcon from "@mui/icons-material/Person";

import { useNavigation } from "../contexts/NavigationContext";

import { getStatusColor } from "../utils/statusHelpers";
import { formatDate, formatTime } from "../utils/formatHelpers"; // Assuming you'll create this

function EngineerDetailPage({ engineerId, onBack }) {
  const { selectJob } = useNavigation();

  const [engineer, setEngineer] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [isLoadingEngineer, setIsLoadingEngineer] = useState(true);
  const [isLoadingJobs, setIsLoadingJobs] = useState(true);

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editedEngineer, setEditedEngineer] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Job menu
  const [jobMenuAnchor, setJobMenuAnchor] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);

  // Fetch engineer details
  useEffect(() => {
    setIsLoadingEngineer(true);
    const engineerDocRef = doc(db, "users", engineerId);

    const fetchEngineer = async () => {
      try {
        const docSnap = await getDoc(engineerDocRef);
        if (docSnap.exists()) {
          const engineerData = { id: docSnap.id, ...docSnap.data() };
          setEngineer(engineerData);
          setEditedEngineer(engineerData);
        } else {
          console.error("No such engineer document!");
          setEngineer(null);
        }
      } finally {
        setIsLoadingEngineer(false);
      }
    };

    fetchEngineer();
  }, [engineerId]);

  // Fetch engineer's jobs
  useEffect(() => {
    if (!engineer) return;

    setIsLoadingJobs(true);

    const jobsQuery = query(
      collection(db, "jobs"),
      where("engineerId", "==", engineer.id),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(
      jobsQuery,
      (querySnapshot) => {
        const jobsList = [];
        querySnapshot.forEach((doc) => {
          jobsList.push({ id: doc.id, ...doc.data() });
        });
        setJobs(jobsList);
        setIsLoadingJobs(false);
      },
      (err) => {
        console.error("Error fetching jobs:", err);
        setIsLoadingJobs(false);
      }
    );

    return () => unsubscribe();
  }, [engineer]);

  // Calculate statistics
  const stats = useMemo(() => {
    const total = jobs.length;
    const completed = jobs.filter(j => j.status === "Completed").length;
    const active = jobs.filter(j => !["Completed", "Incomplete"].includes(j.status)).length;
    const completionRate = total > 0 ? ((completed / total) * 100).toFixed(1) : 0;

    return { total, completed, active, completionRate };
  }, [jobs]);

  // Handle edit
  const handleEditOpen = () => {
    setEditedEngineer(engineer);
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditedEngineer(engineer);
  };

  const handleEditSave = async () => {
    setIsSaving(true);
    try {
      const engineerRef = doc(db, "users", engineerId);
      await updateDoc(engineerRef, {
        firstName: editedEngineer.firstName || "",
        lastName: editedEngineer.lastName || "",
      });

      setEngineer(editedEngineer);
      setEditDialogOpen(false);
      setSnackbar({ open: true, message: "✓ Engineer updated successfully!", severity: "success" });
    } catch (error) {
      console.error("Error updating engineer:", error);
      setSnackbar({ open: true, message: "Failed to update engineer", severity: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle delete
  const handleDeleteOpen = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await deleteDoc(doc(db, "users", engineerId));
      setSnackbar({ open: true, message: "✓ Engineer deleted successfully!", severity: "success" });
      setTimeout(() => onBack(), 1500);
    } catch (error) {
      console.error("Error deleting engineer:", error);
      setSnackbar({ open: true, message: "Failed to delete engineer", severity: "error" });
      setIsDeleting(false);
    }
  };

  // Handle job menu
  const handleJobMenuOpen = (event, job) => {
    setJobMenuAnchor(event.currentTarget);
    setSelectedJob(job);
  };

  const handleJobMenuClose = () => {
    setJobMenuAnchor(null);
    setSelectedJob(null);
  };

  const handleViewJob = () => {
    if (selectedJob) {
      selectJob(selectedJob.id);
    }
    handleJobMenuClose();
  };

  if (isLoadingEngineer) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (!engineer) {
    return (
      <Box>
        <Button onClick={onBack} startIcon={<ArrowBackIcon />} sx={{ mb: 2 }}>
          Back to Engineers
        </Button>
        <Alert severity="error">Engineer not found.</Alert>
      </Box>
    );
  }

  const engineerDisplayName =
    (engineer.firstName || engineer.lastName)
      ? `${engineer.firstName || ""} ${engineer.lastName || ""}`.trim()
      : engineer.email || "Engineer";

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              bgcolor: "secondary.main",
              fontSize: "1.75rem",
              fontWeight: 700,
            }}
          >
            {engineerDisplayName[0]?.toUpperCase() || "E"}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              {engineerDisplayName}
            </Typography>
            <Chip icon={<EngineeringIcon />} label="Engineer" size="small" color="secondary" />
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onBack}>
              Back
            </Button>
            <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEditOpen}>
              Edit
            </Button>
            <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleDeleteOpen}>
              Delete
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* Statistics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: "linear-gradient(135deg, rgba(37,99,235,0.15), rgba(15,23,42,0.95))",
            border: "1px solid rgba(59,130,246,0.35)",
          }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ bgcolor: "primary.main" }}>
                  <AssignmentIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Jobs
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: "linear-gradient(135deg, rgba(16,185,129,0.18), rgba(15,23,42,0.9))",
            border: "1px solid rgba(34,197,94,0.35)",
          }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ bgcolor: "success.main" }}>
                  <CheckCircleIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {stats.completed}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: "linear-gradient(135deg, rgba(56,189,248,0.18), rgba(15,23,42,0.9))",
            border: "1px solid rgba(59,130,246,0.3)",
          }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ bgcolor: "info.main" }}>
                  <PendingActionsIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {stats.active}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Jobs
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: "linear-gradient(135deg, rgba(139,92,246,0.18), rgba(15,23,42,0.9))",
            border: "1px solid rgba(139,92,246,0.35)",
          }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ bgcolor: "secondary.main" }}>
                  <TrendingUpIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {stats.completionRate}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Success Rate
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Engineer Details */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, border: "1px solid rgba(148,163,184,0.2)" }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Contact Information
            </Typography>

            <Stack spacing={2.5}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Avatar sx={{ bgcolor: "rgba(59,130,246,0.1)" }}>
                  <PersonIcon sx={{ color: "primary.main" }} />
                </Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Full Name
                  </Typography>
                  <Typography variant="body1">
                    {engineerDisplayName}
                  </Typography>
                </Box>
              </Box>

              <Divider />

              <Box sx={{ display: "flex", gap: 2 }}>
                <Avatar sx={{ bgcolor: "rgba(16,185,129,0.1)" }}>
                  <EmailIcon sx={{ color: "success.main" }} />
                </Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Email Address
                  </Typography>
                  <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
                    {engineer.email || "Not provided"}
                  </Typography>
                </Box>
              </Box>

              <Divider />

              <Box sx={{ display: "flex", gap: 2 }}>
                <Avatar sx={{ bgcolor: "rgba(139,92,246,0.1)" }}>
                  <EngineeringIcon sx={{ color: "secondary.main" }} />
                </Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Role
                  </Typography>
                  <Chip label={engineer.role || "Engineer"} size="small" color="secondary" />
                </Box>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Job History */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, border: "1px solid rgba(148,163,184,0.2)", maxHeight: "70vh", display: "flex", flexDirection: "column" }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Job History ({jobs.length})
            </Typography>

            {isLoadingJobs ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
                <CircularProgress size={32} />
              </Box>
            ) : jobs.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <AssignmentIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Jobs Yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No jobs have been assigned to this engineer
                </Typography>
              </Box>
            ) : (
              <List sx={{ overflow: "auto", flexGrow: 1 }}>
                {jobs.map((job) => {
                  const scheduled = job.scheduledDateTime || job.scheduledDate;
                  const scheduledDate = formatDate(scheduled);
                  const scheduledTime = formatTime(scheduled);

                  return (
                    <ListItem
                      key={job.id}
                      divider
                      sx={{
                        py: 1.5,
                        px: 2,
                        borderRadius: 2,
                        mb: 0.5,
                        transition: "all 0.2s",
                        "&:hover": {
                          bgcolor: "rgba(56,189,248,0.08)",
                        },
                      }}
                      secondaryAction={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Chip
                            label={job.status || "Unknown"}
                            color={getStatusColor(job.status)}
                            size="small"
                          />
                          <IconButton size="small" onClick={(e) => handleJobMenuOpen(e, job)}>
                            <MoreVertIcon />
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemText
                        primary={
                          <Typography sx={{ fontWeight: 600 }}>
                            {job.jobTemplateName || "Job"}{" "}
                            {job.jobReferenceNumber && `#${job.jobReferenceNumber}`}
                          </Typography>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" color="text.secondary">
                              👤 {job.customerName || "No customer"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              🚗 {job.vehicleReg || "No vehicle"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              📅 {scheduledDate} {scheduledTime && `at ${scheduledTime}`}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Engineer Details</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="First Name"
              fullWidth
              value={editedEngineer.firstName || ""}
              onChange={(e) => setEditedEngineer({ ...editedEngineer, firstName: e.target.value })}
            />
            <TextField
              label="Last Name"
              fullWidth
              value={editedEngineer.lastName || ""}
              onChange={(e) => setEditedEngineer({ ...editedEngineer, lastName: e.target.value })}
            />
            <Alert severity="info">Email cannot be changed</Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained" disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteClose}>
        <DialogTitle>Delete Engineer?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone.
          </Alert>
          <Typography>
            Are you sure you want to delete <strong>{engineerDisplayName}</strong>?
          </Typography>
          {jobs.length > 0 && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              Warning: This engineer has {jobs.length} job(s) assigned.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={isDeleting}>
            {isDeleting ? "Deleting..." : "Delete Engineer"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Job Menu */}
      <Menu anchorEl={jobMenuAnchor} open={Boolean(jobMenuAnchor)} onClose={handleJobMenuClose}>
        <MenuItem onClick={handleViewJob}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Job Details</ListItemText>
        </MenuItem>
      </Menu>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default EngineerDetailPage;